import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { formatINR, formatDate } from "@/lib/format";
import { CategorySelect } from "@/components/categories/CategorySelect";
import { toast } from "sonner";
import { Plus, Trash2, Repeat, Pause, Play } from "lucide-react";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const FREQUENCIES = ["daily", "weekly", "monthly", "yearly"];

function RuleRow({ rule: r, accountName, onToggle, onDelete }) {
  const typeCls = r.type === "income" ? "text-[#2E6F40]" : r.type === "transfer" ? "text-[#A67C00]" : "text-[#D94F3D]";
  const accountLabel = r.type === "transfer"
    ? `${accountName(r.account_id)} → ${accountName(r.to_account_id)}`
    : (r.account_id ? accountName(r.account_id) : "");
  return (
    <tr className={`border-b border-[#EAE7DD] ${!r.active ? "opacity-50" : ""}`}>
      <td className="px-4 py-3">
        <div className="font-sans">{r.description || r.category}</div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#6C7565]">{r.category}{accountLabel ? ` · ${accountLabel}` : ""}</div>
      </td>
      <td className={`px-4 py-3 font-mono text-[11px] uppercase tracking-wider ${typeCls}`}>{r.type}</td>
      <td className="px-4 py-3 text-right num">{formatINR(r.amount)}</td>
      <td className="px-4 py-3 font-mono text-[11px] uppercase tracking-wider">{r.frequency}</td>
      <td className="px-4 py-3 num">{formatDate(r.next_run)}</td>
      <td className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest">
        <span className={r.active ? "text-[#2E6F40]" : "text-[#6C7565]"}>{r.active ? "Active" : "Paused"}</span>
      </td>
      <td className="px-4 py-3 text-right whitespace-nowrap">
        <button data-testid={`toggle-rule-${r.id}`} onClick={() => onToggle(r)} title={r.active ? "Pause" : "Resume"} className="text-[#6C7565] hover:text-[#1A1F16] mr-3">
          {r.active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        </button>
        <button data-testid={`delete-rule-${r.id}`} onClick={() => onDelete(r.id)} className="text-[#6C7565] hover:text-[#D94F3D]"><Trash2 className="w-4 h-4" /></button>
      </td>
    </tr>
  );
}

export default function Recurring() {
  const [rules, setRules] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    type: "expense", amount: "", category: "Rent", account_id: "", to_account_id: "", description: "",
    frequency: "monthly", next_run: new Date().toISOString().slice(0, 10),
  });

  const load = async () => {
    const [r, a] = await Promise.all([api.get("/recurring"), api.get("/accounts")]);
    setRules(r.data);
    setAccounts(a.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (form.type === "transfer" && (!form.account_id || !form.to_account_id)) {
      toast.error("Pick both From and To accounts for a transfer");
      return;
    }
    try {
      await api.post("/recurring", {
        ...form,
        amount: parseFloat(form.amount),
        account_id: form.account_id || null,
        to_account_id: form.type === "transfer" ? form.to_account_id : null,
        next_run: new Date(form.next_run).toISOString(),
      });
      toast.success("Recurring rule created");
      setOpen(false);
      setForm({ ...form, amount: "", description: "" });
      load();
    } catch { toast.error("Failed to save"); }
  };

  const toggleActive = async (rule) => {
    try {
      const { id, user_id, created_at, last_run, ...payload } = rule;
      await api.put(`/recurring/${rule.id}`, { ...payload, active: !rule.active });
      toast.success(rule.active ? "Paused" : "Resumed");
      load();
    } catch { toast.error("Failed to update"); }
  };

  const [confirm, setConfirm] = useState(null);
  const remove = (id) => {
    const r = rules.find((x) => x.id === id);
    setConfirm({
      title: "Delete recurring rule?",
      detail: `"${r?.description || r?.category || "This rule"}" (${formatINR(r?.amount || 0)} ${r?.frequency || ""}) will stop running and be permanently deleted. Past transactions stay on the books.`,
      run: async () => { await api.delete(`/recurring/${id}`); toast.success("Rule removed"); load(); },
    });
  };

  const accountName = (id) => accounts.find((a) => a.id === id)?.name || "—";
  const selectableAccounts = useMemo(() => accounts.filter((a) => a.type !== "demat"), [accounts]);
  const toAccounts = useMemo(
    () => selectableAccounts.filter((a) => a.id !== form.account_id),
    [selectableAccounts, form.account_id]
  );

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between border-b border-[#1A1F16] pb-6 flex-wrap gap-4">
        <div>
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#6C7565]">Section VII · Automatic</span>
          <h2 className="font-serif text-5xl tracking-tight mt-2">Recurring.</h2>
        </div>
        <button
          data-testid="add-recurring-btn"
          onClick={() => setOpen(true)}
          className="bg-[#1A1F16] text-[#F7F5F0] px-5 py-3 text-xs uppercase tracking-[0.25em] hover:bg-[#2C3628] flex items-center gap-2"
        >
          <Plus className="w-3.5 h-3.5" /> New rule
        </button>
      </div>

      <p className="text-sm text-[#6C7565] max-w-2xl -mt-2">
        Rules run automatically — rent, salary, SIPs. When a rule falls due, the transaction is recorded
        with the scheduled date and you get a notification in the bell.
      </p>

      <div className="border border-[#D8D4C7] bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="recurring-table">
          <thead>
            <tr className="border-b border-[#1A1F16] text-left">
              <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#6C7565]">Rule</th>
              <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#6C7565]">Type</th>
              <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#6C7565] text-right">Amount</th>
              <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#6C7565]">Frequency</th>
              <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#6C7565]">Next run</th>
              <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#6C7565]">Status</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {rules.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-[#6C7565]">
                <Repeat className="w-6 h-6 mx-auto mb-2 opacity-40" />
                No recurring rules yet. Automate your rent, salary or SIPs.
              </td></tr>
            ) : rules.map((r) => (
              <RuleRow key={r.id} rule={r} accountName={accountName} onToggle={toggleActive} onDelete={remove} />
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-[#1A1F16]/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-[#F7F5F0] border border-[#1A1F16] p-8 w-full max-w-md" data-testid="recurring-form">
            <h3 className="font-serif text-3xl tracking-tight mb-6">New recurring rule.</h3>
            <div className="flex gap-2 mb-4">
              {["expense", "income", "transfer"].map((t) => (
                <button
                  key={t}
                  type="button"
                  data-testid={`rule-type-${t}`}
                  onClick={() => setForm({ ...form, type: t, category: t === "expense" ? "Rent" : t === "income" ? "Salary" : "Account Transfer" })}
                  className={`flex-1 py-2 text-[10px] uppercase tracking-widest border ${form.type === t ? "bg-[#1A1F16] text-[#F7F5F0] border-[#1A1F16]" : "border-[#1A1F16]"}`}
                >{t}</button>
              ))}
            </div>
            <label className="block mb-4">
              <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-1">Amount (₹)</span>
              <input type="number" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="rule-amount" className="w-full bg-transparent border-b border-[#1A1F16] py-2 text-2xl font-serif num focus:outline-none focus:border-[#A67C00]" />
            </label>
            <label className="block mb-4">
              <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-1">Category</span>
              <CategorySelect type={form.type} value={form.category} onChange={(c) => setForm({ ...form, category: c })} testid="rule-category" />
            </label>
            <label className="block mb-4">
              <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-1">{form.type === "transfer" ? "From account" : "Account (optional)"}</span>
              <select value={form.account_id} onChange={(e) => setForm({ ...form, account_id: e.target.value })} data-testid="rule-account" className="w-full bg-transparent border-b border-[#1A1F16] py-2 focus:outline-none">
                <option value="">{form.type === "transfer" ? "Select account" : "None"}</option>
                {selectableAccounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </label>
            {form.type === "transfer" && (
              <label className="block mb-4">
                <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-1">To account (e.g. your mutual fund)</span>
                <select value={form.to_account_id} onChange={(e) => setForm({ ...form, to_account_id: e.target.value })} data-testid="rule-to-account" className="w-full bg-transparent border-b border-[#1A1F16] py-2 focus:outline-none">
                  <option value="">Select account</option>
                  {toAccounts.map((a) => <option key={a.id} value={a.id}>{a.name}{a.type === "mutual_fund" ? " (MF)" : ""}</option>)}
                </select>
              </label>
            )}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <label className="block">
                <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-1">Frequency</span>
                <select value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })} data-testid="rule-frequency" className="w-full bg-transparent border-b border-[#1A1F16] py-2 focus:outline-none">
                  {FREQUENCIES.map((f) => <option key={f}>{f}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-1">First run</span>
                <input type="date" required value={form.next_run} onChange={(e) => setForm({ ...form, next_run: e.target.value })} data-testid="rule-next-run" className="w-full bg-transparent border-b border-[#1A1F16] py-2 focus:outline-none" />
              </label>
            </div>
            <label className="block mb-6">
              <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-1">Description</span>
              <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. Flat rent, Netflix, SIP" data-testid="rule-description" className="w-full bg-transparent border-b border-[#1A1F16] py-2 focus:outline-none" />
            </label>
            <div className="flex gap-3">
              <button type="button" onClick={() => setOpen(false)} data-testid="rule-cancel" className="border border-[#1A1F16] px-5 py-3 text-xs uppercase tracking-widest hover:bg-[#EAE7DD]">Cancel</button>
              <button type="submit" data-testid="rule-submit" className="flex-1 bg-[#1A1F16] text-[#F7F5F0] py-3 text-xs uppercase tracking-widest hover:bg-[#2C3628]">Create rule</button>
            </div>
          </form>
        </div>
      )}
      <ConfirmDialog confirm={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}
